import { CoverLot, EmptyCoverLot } from '@/types/CoverLot';
import { decodeHTML } from 'entities';
import { translateAccents } from '@/utils/translateCharacterAccents';
import pickBy from 'lodash/pickBy';
import xss from 'xss';

// use default xss filters and decode html symbols into a text string
// @ts-ignore
const sanitizeHtml = (text: string) => decodeHTML(xss(text));

export const getCategoryIdsFromPathname = (pathname: string = '') => {
    if (pathname === '/catalog/search') {
        return '0';
    }
    const categoryIds = pathname
        .split('/')
        .filter((x) => Number(x))
        .join(',');
    return categoryIds;
};

export const getCombinationCategoryIdsFromPath = (params: any, queryParams: any) => {
    if (!params.categoryId2) {
        return '';
    }
    let ids = [params.categoryId, params.categoryId2];
    for (let key in queryParams) {
        ids.push(queryParams[key]);
    }
    ids.sort(function (a, b) {
        return a - b;
    });
    return ids.join(',');
};

// gets the last categoryid from the path
export const getFinalCategoryIdFromPathname = (pathname: string = '') => {
    if (pathname === '/catalog/search') {
        return '';
    }
    const categoryIds = pathname.split('/').filter((x) => Number(x));
    if (categoryIds.length > 0) {
        return categoryIds[categoryIds.length - 1];
    }
    return '';
};

// retrieves the last string from a path, which in some scenarios will be the lowest-level category desired
// returns category name formatted to be passed for wordpress api search
export const getCategoryNameFromPathname = (pathname: string = '') => {
    const categoryName = pathname.split('/').filter((x) => x && !Number(x));
    const cleanedUpCategoryName = categoryName.length ? categoryName[categoryName.length - 1].replace(/-/g, ' ') : '';
    return cleanedUpCategoryName;
};

export const getCategoryNameFromPathnameWithHyphens = (pathname: string = '') => {
    const categoryName = pathname.split('/').filter((x) => x && !Number(x));
    const cleanedUpCategoryName = categoryName.length ? categoryName[categoryName.length - 1] : '';
    return cleanedUpCategoryName;
};

export const getRawCategoryNameFromPathname = (pathname: string = '') => {
    const categoryName = pathname.split('/').filter((x) => x && !Number(x));
    return categoryName[categoryName.length - 1];
};

export const selectRelatedSearchCarouselUrl = (displayName, relatedCategoryId, url) => {
    return relatedCategoryId ? `/c/${url}/${relatedCategoryId}/` : `/search/?keyword=${displayName}`;
};

export const formatLocationName = (name: string): string => {
    if (!Boolean(name)) {
        return '';
    }
    const words = name.split('-');

    for (let i = 0; i < words.length; i++) {
        words[i] = words[i][0].toUpperCase() + words[i].substr(1);
    }

    return words.join(' ');
};

export const selectSuggestedCategoryCarouselUrl = (
    categoryId: string,
    categoryId2: number,
    categoryName: string,
    filterFacetId: number,
    location: any,
    url: string
) => {
    if (categoryId2) {
        return location.search.includes('?category_id')
            ? `${location.pathname}${location.search},${filterFacetId}`
            : `${location.pathname}?category_id=${filterFacetId}`;
    }
    return `/c/${categoryName}/${url}/${categoryId}/${filterFacetId}/`;
};

export const addFastlySuffixToImgUrl = (url, size) => {
    return `${url}?auto=webp&format=pjpg&width=${size}`;
};

export const getIdFromPath = (path: string) => {
    if (!path) {
        return 0;
    }

    const pathString = `${path}`;
    const cleaned = pathString.replace(
        /^\/?(catalog(_gallery)?|item|auctioneer|catinfot|console|checkout|terms)\/?/,
        ''
    );
    let urlFrags = cleaned.split('_')[0];

    if (urlFrags.includes('/page')) {
        urlFrags = urlFrags.split('/')[0];
    }

    const idMatches = urlFrags.match(/^(\d+)\/?$/);
    return idMatches ? Number(idMatches[1]) || 0 : 0;
};

// https://stackoverflow.com/a/1714899/148256
// Returns a string from the object
export const toQueryString = (obj: any) => {
    const arr = [];

    // Checks if object has prototypes, if not, creates a new object with default prototypes
    if (Boolean(obj) && !Boolean(obj.__proto__)) {
        obj = { ...obj };
    }

    for (const p in obj) {
        if (obj.hasOwnProperty(p)) {
            arr.push(`${encodeURIComponent(p)}=${encodeURIComponent(obj[p])}`);
        }
    }
    arr.sort();
    return arr.join('&');
};

export const sellerIdFromPath = (path: string) => {
    if (!path) {
        return 0;
    }
    let sellerId = Number(path);

    if (!sellerId) {
        sellerId = Number(path.split('-')[0]);
    }
    return sellerId;
};

export const logoUrl = 'https://p1.liveauctioneers.com/dist/images/logo_og.jpg?auto=webp&format=pjpg';
export const signupLadySmallUrl =
    'https://p1.liveauctioneers.com/dist/images/SignupLadySmall.png?auto=webp&format=pjpg';
export const signupLadyPlaceholderUrl =
    'https://p1.liveauctioneers.com/dist/images/SignupLadySmall.png?auto=webp&format=pjpg&quality=15';
export const mobileLogo = 'https://p1.liveauctioneers.com/dist/images/logo-1024.png';
export const enableBrowserNotificationsUrl =
    'https://help.liveauctioneers.com/article/359-how-do-i-enable-browser-notifications';

export const urlSafeTitle = (title: string = '') => {
    let cleanedAndDecodedHtml = sanitizeHtml(title);
    cleanedAndDecodedHtml = translateAccents(cleanedAndDecodedHtml);
    const safe = cleanedAndDecodedHtml
        .trim()
        .toLowerCase() // & to and
        .replace(/&/g, 'and') // Replace , * ; : ' " to hyphen
        .replace(/[,*;:'"]+/g, '-') // Replace `- -` to single hyphen
        .replace(/-*\s+-*/g, '-') // Remove special characters
        .replace(/[^a-zA-Z0-9-_-]/g, '') // many hyphen to one
        .replace(/--+/g, '-') // Remove hyphen if it is the last character
        .replace(/-+$/, '');
    const urlEncoded = encodeURIComponent(safe).toLowerCase(); // lower case it again since the URI encoded values can be upper case
    return urlEncoded;
};

export const setQueryParams = (base: string, params: any = {}) => {
    let cleanedBase = base.replace(/\?.*/, '');
    const queryString = toQueryString(params);
    return queryString === '' ? cleanedBase : `${cleanedBase}?${queryString}`;
};

const buildSearchUrl = (base, params?: any) => {
    const queryString = toQueryString(params);
    return queryString === '' ? base : `${base}?${queryString}`;
};

export const getAuctionCalendarBaseUrl = () => '/catalog/search/';

export const getAuctionCalendarUrlWithParams = (params?: any) => {
    const base = getAuctionCalendarBaseUrl();
    return buildSearchUrl(base, params);
};

export const getSearchBaseUrl = () => '/search/';

export const getSearchUrlWithParams = (params: any) => {
    const base = getSearchBaseUrl();
    return buildSearchUrl(base, params);
};

export const getCategoryUrlWithParams = (base: string, params: any) => {
    return buildSearchUrl(base, params);
};

export const getCategoryFacetLink = (base: string, params: any) => {
    base = base === '' || base === undefined ? '/c/' : `/c/${base}/`;
    return buildSearchUrl(base, params);
};

//keywords: string could just be 'rings' or could be 'paintings/oil'
//categoryId: string. Could be '96' or could be '10,25830'(which is a subcategory)
export const getCategoryPageUrl = (keywords: string, categoryId: string, params?: any) => {
    const url = `/c/${keywords}/${categoryId.replace(',', '/')}/`;
    return buildSearchUrl(url, params);
};

export const getCombinedCategoryPageUrl = (firstCategory: any, secondCategory: any) => {
    const url = `/c/${urlSafeTitle(firstCategory.name)}/${urlSafeTitle(secondCategory.name)}/${firstCategory.id}/${
        secondCategory.id
    }/`;
    return buildSearchUrl(url);
};

const removeLastSlash = (text: string = '') => {
    if (text?.length && text[text.length - 1] === '/') {
        return text.substring(0, text.length - 1);
    }
    return text;
};

export const getACHTermsAndConditionsUrl = () => '/achtermsandconditions';
export const getAuctionResultsUrl = () => '/auction-results';
export const getAuctioneerDirectoryUrl = () => '/auctioneer/directory/';
export const getLiveAuctioneersCareersUrl = () => 'https://jobs.lever.co/liveauctioneers';
export const getCatalogUrl = (id: number, title: string) => `/catalog/${id}_${urlSafeTitle(title)}/`;
export const getCategoryLandingPageBaseUrl = () => '/c/';
export const isCategoryPage = (path: string = '') => path.includes(getCategoryLandingPageBaseUrl());
export const getCatalogTermsUrl = (id: number, title: string) => `/terms/${id}_${urlSafeTitle(title)}`;
export const getCheckoutUrl = (catalogId: number) => `/checkout/${catalogId}`;
export const getConsignmentsUrl = () => '/consignments';
export const getCookiePolicyUrl = () => '/cookie-policy';
export const getDiscoverPageUrl = (topic: string) => removeLastSlash(`/discover/${topic}`);
export const getDisputeHelpUrl = () => 'https://help.liveauctioneers.com/article/163-how-to-handle-a-dispute';
export const getFeaturedCatalogsUrl = () => '/featured-auctions';
export const getFollowedSellersUrl = () => '/dashboard/followed-auctioneers';
export const getHomeUrl = () => '/';
export const getHowAuctionsWorkUrl = () => '/how-auctions-work';
export const getIntellectualPropertyUrl = () => '/intellectualproperty';
export const getItemUrl = (id: number, title: string) => (Boolean(id) ? `/item/${id}_${urlSafeTitle(title)}` : '');
export const isItemPage = (path: string = '') => path.includes('/item/');
export const getLiveBiddingUrl = (catalogId: number) => `/console/${catalogId}`;
export const getMessagesUrl = () => '/dashboard/messages';
export const getMyAuctionsUrl = () => '/dashboard/my-auctions';
export const getMyBidsUrl = () => '/dashboard/my-bids';
export const getNewsUrl = (base: string = 'https://www.liveauctioneers.com') => `${base}/news/`;
export const getNotFoundUrl = () => '/not-found';
export const getNotificationsUrl = (section?: string) =>
    `/dashboard/emails-and-notifications${section ? `?section=${section}` : ''}`;
export const getRecommendedItemsUrl = () => '/recommended';
export const getRecentItemsUrl = () => '/recent';
export const getReviewUrl = (catalogId: number) => `/review/${catalogId}`;
export const getPreferencesUrl = () => '/dashboard/profile?tab=preferences';
export const getPriceGuideBaseUrl = () => '/price-guide/';
export const isPriceGuidePage = (path: string = '') => path.includes(getPriceGuideBaseUrl());
export const getPriceGuideUrl = (name: string, id: number) => `${getPriceGuideBaseUrl()}${name}/${id}/`;
export const getPrivacyUrl = () => '/privacy';
export const getProfileUrl = (provider?: number) =>
    provider ? `/dashboard/profile?provider=${provider}` : '/dashboard/profile';
export const getSavedItemsUrl = () => '/dashboard/saved-items';
export const getSavedSearchesUrl = (append?: string) => `/dashboard/search-and-alerts/${append ? `${append}/` : ''}`;
export const getSellPageUrl = () => 'https://www.liveauctioneers.com/pages/sell/';
export const getSellerUrl = (id: number, title: string) => `/auctioneer/${id}/${urlSafeTitle(title)}/`;
export const getSellerReviewsUrl = (id: number, title: string, filter: string = '') =>
    filter
        ? `/auctioneer/${id}/${urlSafeTitle(title)}/reviews?filter=${filter}`
        : `/auctioneer/${id}/${urlSafeTitle(title)}/reviews`;
export const getStoreFrontUrl = (sellerId: number) => `/seller/${sellerId}`;
export const getSupportUrl = () => 'https://help.liveauctioneers.com';
export const getTerminologyUrl = () => '/terminology';
export const getTermsAndConditionsUrl = () => '/termsandconditions';
export const getTermsAndConditionsPaymentsUrl = () => '/termsandconditionspayments';
export const getSellerTermsAndConditionsPaymentsUrl = () =>
    'https://support.liveauctioneers.com/article/92-liveauctioneers-seller-terms-conditions';
export const getFeeScheduleUrl = () =>
    'https://support.liveauctioneers.com/article/182-traditional-auction-vs-online-only-auction';
export const getWonItemsPageUrl = () => '/dashboard/won-items';
export const getTaxCertManagementUrl = () => '/dashboard/profile?tab=tax-cert-management';
export const getManageCategoryNotificationsUrl = (bidderEmail: string) =>
    `https://link.liveauctioneers.com/manage/4x3/category-management-v2?email=${bidderEmail}`;

export const addImageQueryParams = (
    url: string = '',
    width?: number,
    height?: number,
    quality?: number,
    imageVersion?: number,
    fit?: string
) => {
    if (!url) {
        return '';
    }
    const correctImageUrl = url.replace('http://', 'https://');
    const size = [];
    if (width) {
        size.push(`width=${width}&`);
    }
    if (height) {
        size.push(`height=${height}&`);
    }
    if (quality) {
        size.push(`quality=${quality}&`);
    }
    if (imageVersion) {
        size.push(`imageVersion=${imageVersion}&`);
    }
    if (fit) {
        size.push(`fit=${fit}&`);
    }
    return `${correctImageUrl}?${size.join('')}auto=webp&format=pjpg`;
};

export const getAuctioneerLogoUrl = (auctioneer: string | null | undefined = '', width: number = 110) => {
    return `https://images.liveauctioneers.com/houses/logos/lg/${auctioneer}_large.jpg?auto=webp&format=pjpg&width=${width}&quality=80`;
};

export const getAuctioneerLogoUrlPlaceholder = (auctioneer: string | null | undefined = '', width: number = 110) => {
    return `${getAuctioneerLogoUrl(auctioneer, width)}&quality=15`;
};

export const getAllUpcomingAuctionsUrl = () => getAuctionCalendarUrlWithParams();

type GetImageUrlParams = {
    backgroundColor?: string;
    blur?: any;
    canvas?: string;
    catalogId?: number;
    crop?: any;
    fit?: string;
    height?: number;
    imageNumber?: number;
    imageVersion?: number;
    itemId?: number;
    quality?: any;
    sellerId?: number;
    width?: number;
};

export const getImageUrl = ({
    backgroundColor,
    blur,
    canvas,
    catalogId,
    crop,
    fit,
    height,
    imageNumber = 1,
    imageVersion = 1,
    itemId,
    quality,
    sellerId,
    width,
}: GetImageUrlParams) => {
    if (!sellerId || !catalogId || !itemId || !imageNumber) {
        return '';
    }

    return addImageParams({
        backgroundColor,
        blur,
        canvas,
        crop,
        fit,
        height,
        quality,
        url: `https://p1.liveauctioneers.com/${sellerId}/${catalogId}/${itemId}_${imageNumber}_x.jpg`,
        version: imageVersion,
        width,
    });
};

type addImageParamTypes = {
    backgroundColor?: string;
    blur?: any;
    canvas?: string;
    crop?: any;
    fit?: string;
    height?: number;
    quality?: number;
    url?: string;
    version?: number;
    width?: number;
};

export const addImageParams = ({
    backgroundColor,
    blur,
    canvas,
    crop,
    fit,
    height,
    quality = 70,
    url = '',
    version,
    width,
}: addImageParamTypes) => {
    const query = pickBy({
        auto: 'webp',
        'bg-color': backgroundColor,
        blur,
        canvas,
        crop,
        fit,
        format: 'pjpg',
        height,
        quality,
        version,
        width,
    });

    return `${url.replace('http://', 'https://')}?${toQueryString(query)}`.trim();
};

export const getPlaceholderItemImageUrl = (
    sellerId: number,
    catalogId: number,
    itemId: number,
    imageNumber: number,
    imageVersion: number = 1
) =>
    getImageUrl({
        catalogId,
        fit: 'bounds',
        height: 612,
        imageNumber,
        imageVersion,
        itemId,
        quality: 10,
        sellerId,
        width: 612,
    });

export const getMediumItemImageUrl = (
    sellerId: number,
    catalogId: number,
    itemId: number,
    imageNumber: number,
    imageVersion: number = 1,
    quality: number = 70
) =>
    getImageUrl({
        catalogId,
        fit: 'bounds',
        height: 512,
        imageNumber,
        imageVersion,
        itemId,
        quality,
        sellerId,
        width: 512,
    });

export const getCuratorImageUrl = (baseUrl: string, curatorId: number, curatorImageFileName: string) =>
    `${baseUrl}/${curatorId}/${encodeURIComponent(curatorImageFileName)}?width=170&crop=1:1,smart`;

export const getItemCardImageUrl = (
    sellerId: number,
    catalogId: number,
    itemId: number,
    imageNumber: number,
    imageVersion: number
) => getImageUrl({ catalogId, fit: 'bounds', height: 280, imageNumber, imageVersion, itemId, sellerId, width: 280 });

export const getMegaNavImageUrlLarge = (category: string) =>
    `https://p1.liveauctioneers.com/dist/images/desktop-${category}.jpg?auto=webp&format=pjpg&height=382`;

export const getMegaNavImageUrlSmall = (category: string) =>
    `https://p1.liveauctioneers.com/dist/images/tablet-${category}.jpg?auto=webp&format=pjpg&height=288`;

export const getMegaNavImageUrlLargePlaceholder = (category: string) =>
    `https://p1.liveauctioneers.com/dist/images/desktop-${category}.jpg?auto=webp&format=pjpg&height=382&quality=15`;

export const getMegaNavImageUrlSmallPlaceholder = (category: string) =>
    `https://p1.liveauctioneers.com/dist/images/tablet-${category}.jpg?auto=webp&format=pjpg&height=288&quality=15`;

export const getDeskSupportLink = () => 'https://help.liveauctioneers.com/';

export const getAdQuestionUrl = () => 'https://www.surveymonkey.com/r/KFCCVSX';

export const getSearchPageMostPopular = () => getSearchUrlWithParams({ page: 1, sort: '-popularity' });

const categoryLandingIds = {
    art: 1,
    asian: 158,
    collectibles: 2,
    fashion: 3,
    furniture: 4,
    'home-garden': 5,
    jewelry: 6,
};

export const getCategoryLandingPageUrlFromCategoryName = (category: string) => {
    const categoryId = categoryLandingIds[category];
    return categoryId
        ? `${getCategoryLandingPageBaseUrl()}${category}/${categoryId}/`
        : getCategoryLandingPageBaseUrl();
};

export const separateURL = (url: string) => {
    const match = url.match(/^(https?:)\/\/(([^:/?#]*)(?::([0-9]+))?)([/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/);
    if (match === null) {
        return {
            host: '',
            path: '',
        };
    }
    return {
        host: match[2],
        path: match[5],
    };
};

export const getSearchAuctioneerUrl = (sellerId: number) =>
    getSearchUrlWithParams({
        house_id: `${sellerId}`,
        page: 1,
        sort: '-popularity',
    });

export const getSearchAuctioneerSoldUrl = (sellerId: number) =>
    getSearchUrlWithParams({
        house_id: `${sellerId}`,
        page: 1,
        status: 'archive',
    });

export const getUpcomingTitleSearchParams = (title: string = '', sellerId: number = 0) => {
    const keyword = title
        .split(/\s+/g)
        .map((word) => `${word}`)
        .join(' OR ')
        .replace(/"/g, '\\"');
    const params: any = { keyword, status: ['online', 'live', 'upcoming'] };
    if (sellerId) {
        params.house_id = sellerId.toString();
    }
    return params;
};

export const getSimilarItemsUrl = (title: string = '', sellerId: number = 0) => {
    const params = getUpcomingTitleSearchParams(title, sellerId);
    return getSearchUrlWithParams(params);
};

export const getCoverLots = (coverLots: CoverLot[], height?: number) => {
    const [first, second, third] = coverLots.length ? coverLots : [EmptyCoverLot, EmptyCoverLot, EmptyCoverLot];
    const primaryImage = addImageQueryParams(
        first?.url,
        (height || 270) * 1.5,
        height || 270,
        0,
        first?.imageVersion,
        'bounds'
    );
    const primaryImagePlaceholder = addImageQueryParams(
        first?.url,
        (height || 270) * 1.5,
        height || 270,
        15,
        first?.imageVersion,
        'bounds'
    );
    const primaryItemUrl = getItemUrl(first?.itemId, first?.title);

    const secondaryImage = addImageQueryParams(
        second?.url,
        (height || coverLots?.length === 2 ? 270 : 170) * 1.5,
        height || coverLots?.length === 2 ? 270 : 170,
        0,
        second?.imageVersion,
        'bounds'
    );
    const secondaryImagePlaceholder = addImageQueryParams(
        second?.url,
        (height || coverLots?.length === 2 ? 270 : 170) * 1.5,
        height || coverLots?.length === 2 ? 270 : 170,
        15,
        second?.imageVersion,
        'bounds'
    );
    const secondaryItemUrl = getItemUrl(second?.itemId, second?.title);

    const tertiaryImage = addImageQueryParams(
        third?.url,
        (height || 270) * 1.5,
        height || 170,
        0,
        third?.imageVersion,
        'bounds'
    );
    const tertiaryImagePlaceholder = addImageQueryParams(
        third?.url,
        (height || 270) * 1.5,
        height || 170,
        15,
        third?.imageVersion,
        'bounds'
    );
    const tertiaryItemUrl = getItemUrl(third?.itemId, third?.title);

    return {
        primaryImage,
        primaryImagePlaceholder,
        primaryItemUrl,
        secondaryImage,
        secondaryImagePlaceholder,
        secondaryItemUrl,
        tertiaryImage,
        tertiaryImagePlaceholder,
        tertiaryItemUrl,
    };
};

const getWebviewsUrl = (path: string, deployment: string) => {
    let domain = `https://webviews${deployment === 'prod' ? '' : `-${deployment}`}.liveauctioneers.com`;
    return `${domain}/${path}`;
};

export const getPayrixAddCreditCardUrl = (deployment: string, address: any, customerId?: string) => {
    let url = getWebviewsUrl('addcreditcard/3', deployment);
    let queryParam = '';
    if (address) {
        queryParam = `address1=${encodeURIComponent(address.address1)}&address2=${encodeURIComponent(
            address.address2
        )}&state=${encodeURIComponent(address.state)}&countryCode=${encodeURIComponent(
            address.countryCode
        )}&city=${encodeURIComponent(address.city)}&postalCode=${encodeURIComponent(address.postalCode)}`;
    }
    if (customerId) {
        queryParam = `${queryParam}${queryParam ? '&' : ''}customerId=${encodeURIComponent(customerId)}`;
    }
    if (queryParam) {
        url = `${url}?${queryParam}`;
    }
    return url;
};

export const getPaysafeAddCreditCardUrl = (deployment: string, address: any, customerId?: string) => {
    let url = getWebviewsUrl('addcreditcard/4', deployment);
    let queryParam;
    if (address) {
        queryParam = `address1=${encodeURIComponent(address.address1)}&address2=${encodeURIComponent(
            address.address2
        )}&state=${encodeURIComponent(address.state)}&countryCode=${encodeURIComponent(
            address.countryCode
        )}&city=${encodeURIComponent(address.city)}&postalCode=${encodeURIComponent(address.postalCode)}`;
    }
    if (customerId) {
        queryParam = `${queryParam}${queryParam ? '&' : ''}customerId=${encodeURIComponent(customerId)}`;
    }
    if (queryParam) {
        url = `${url}?${queryParam}`;
    }
    return url;
};

export const getEkataTermsUrl = () => 'https://ekata.com/privacy/';

export const getPayrixTermsUrl = () => 'https://portal.payrix.com/terms';

export const getTaxCertCaptureUrl = (deployment: string, token: string, shipZone: string, userData: object) => {
    // exit with a falsy value if we are missing data.
    if (!token || !shipZone) {
        return '';
    }
    let url = getWebviewsUrl('taxcertcapture', deployment);
    let queryParam = '';
    if (token && shipZone) {
        queryParam = `token=${token}&shipZone=${shipZone}`;
    }
    if (userData) {
        queryParam = `${queryParam}&userData=${encodeURIComponent(JSON.stringify(userData))}`;
    }
    if (queryParam) {
        url = `${url}?${queryParam}`;
    }

    return url;
};

export const getCertCapturePageUrl = (shipZone: string) => {
    const url = '/certcapture';
    let queryParam = '';
    if (shipZone) {
        queryParam = `?shipZone=${shipZone}`;
    }
    return `${url}${queryParam}`;
};

export type AuctionsNearMeGeoAreaParams = {
    geoAreaId: number;
    geoAreaName: string;
};

export const getAuctionsNearMePageUrl = (params?: AuctionsNearMeGeoAreaParams) => {
    let url = '/find-auctions-near-me/';
    if (params) {
        url += `${params.geoAreaId}/${params.geoAreaName.replace(/\s+/g, '-').toLowerCase()}/`;
    }
    return url;
};
